import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";
import { api, formatAWG, usdApprox } from "@/lib/api";
import { Reveal } from "@/components/util/Reveal";
import { ProductCard } from "@/components/cards/ProductCard";
import { BrandDot } from "@/components/util/BrandDot";

const PERKS_FALLBACK = [
  { icon: "Truck", title: "Delivered To You", sub: "Across Aruba, fast & reliable" },
  { icon: "PackageCheck", title: "Ready To Go", sub: "Pre-configured & tested" },
  { icon: "ShieldCheck", title: "Warranty Support", sub: "Manufacturer warranty + local assistance" },
  { icon: "Headphones", title: "Setup Service (Optional)", sub: "Software installation, data transfer & optimization" },
  { icon: "MessagesSquare", title: "Need Help Choosing?", sub: "Chat with us on WhatsApp — we're here to help!" },
];

const isHelpChoosing = (title) => {
  const t = (title || "").trim().toLowerCase();
  return t.startsWith("need help") || t.includes("chat with us");
};

// Category accent markers (Sep 2026, owner-approved): numeral color per
// collection slot — 01 cyan, 02 electric lime (#BFEA00, brightened from
// the muted olive #A8B545 per owner direction), 03 Ur orange,
// 04 magenta. ONLY the numeral (+ its badge border/halo via --ws-accent)
// is color-coded; titles, descriptions, pricing and CTA keep the
// standard palette. This token is workstation-specific by design — no
// other component reads it.
const WS_ACCENTS = ["#22D3EE", "#BFEA00", "#FF6600", "#E93D82"];

export const LaptopSetups = ({ laptops }) => {
  const navigate = useNavigate();
  const [useCases, setUseCases] = useState([]);
  const [perks, setPerks] = useState(PERKS_FALLBACK);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [sectionCta, setSectionCta] = useState({ label: "View All Setups", link: "/laptops" });
  const [addToCartLabel, setAddToCartLabel] = useState("Add to Cart");
  const [intro, setIntro] = useState({
    eyebrow: "Professional Workstations",
    heading: "Built Around The Work",
    accent: "You Actually Do.",
    description: "Purpose-built laptops configured for the way you actually work — selected, prepared and supported by Ur Designs.",
    accentInline: false,
    showDot: false,
  });
  useEffect(() => {
    // EQUIP Option C: homepage grid renders 4 use cases via /api/use-cases.
    // The `laptops` prop is retained for legacy consumers; if the new
    // endpoint fails the fallback renders the old data.
    api.get("/use-cases").then((r) => setUseCases(r.data)).catch(() => {});
    api.get("/settings")
      .then((r) => {
        const d = r.data || {};
        const p = d.laptop_perks;
        if (Array.isArray(p) && p.length > 0) setPerks(p);
        setWhatsappUrl(d.cta_whatsapp_url || "");
        setSectionCta({
          label: d.laptops_view_all_label || d.laptops_section_cta_label || "Explore Workstations",
          link: d.laptops_section_cta_link || "/workstations",
        });
        setAddToCartLabel(d.laptops_home_cta_label || "View Workstation");
        setIntro({
          eyebrow: d.laptops_eyebrow || "Professional Workstations",
          heading: d.laptops_heading || "Built Around The Work",
          accent: d.laptops_heading_accent ?? "You Actually Do.",
          description: d.laptops_hero_description || "Purpose-built laptops configured for the way you actually work — selected, prepared and supported by Ur Designs.",
          accentInline: !!d.laptops_accent_inline,
          showDot: !!d.laptops_show_dot,
        });
      })
      .catch(() => {});
  }, []);

  return (
    <section
      data-testid="laptop-setups"
      id="laptop-setups"
      className="bg-[#0F0F0F] py-28 sm:py-32 relative overflow-hidden"
    >
      {/* Premium workstation showcase (Sep 2026, owner-approved visual
          refinement): pure black negative space — the former orange
          ambient glow blob was removed as decorative noise so the
          colorful artwork carries the section. Nothing else added. */}
      <div className="relative max-w-[1400px] min-[1440px]:max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 min-[1440px]:px-[clamp(2.5rem,4vw,4rem)]">
        {/* Section intro — centered header on top, replaces the former
            left-side intro column. Description is intentionally hard-coded
            to the short overview line so the row of four compact cards
            below reads as a clean product overview, not a detailed pitch. */}
        <Reveal className="text-center max-w-3xl mx-auto">
          <div className="ur-eyebrow">{intro.eyebrow}</div>
          <h2 className="font-display mt-4 text-[36px] sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1]">
            {intro.heading}
            {intro.accent && (
              <> <span className="text-[#FF6600]">{intro.accent}</span></>
            )}
          </h2>
          <p className="mt-4 text-[#A3A3A3] text-[14px] leading-relaxed max-w-xl mx-auto">
            Purpose-built laptops selected for business, content, creative and architecture workflows.
          </p>
        </Reveal>

        {/* Cards row — single horizontal row of four showcase cards on
            desktop (28px separation), 2x2 on tablet (24px), single
            column stack on mobile. Category icons fully removed: the
            artwork itself communicates each category. Hierarchy per
            card: IMAGE → CATEGORY → short description → PRICE → CTA. */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-7">
          {(useCases.length ? useCases : []).map((uc, i) => {
            const hc = uc.resolved_hardware_class || {};
            const price = hc?.windows?.price || hc?.mac?.price || 0;
            const isPlaceholder = !!(hc?.windows?.is_placeholder || hc?.mac?.is_placeholder);
            return (
              <ProductCard
                key={uc.id}
                testId={`laptop-card-${uc.slug}`}
                image={uc.image}
                imageClassName="w-full h-full object-cover"
                name={uc.display_name}
                titleClassName="text-[17px] leading-tight"
                editorialNumber={i + 1}
                editorialAccent={WS_ACCENTS[i % WS_ACCENTS.length]}
                description={uc.byline}
                hoverImage={uc.image_alt || null}
                reveal={{ delay: Math.min(i * 40, 150) }}
                priceFormatted={`AWG ${formatAWG(price)}${isPlaceholder ? " *" : ""}`}
                priceSub={isPlaceholder ? "Pricing under review" : usdApprox(price)}
                primaryCta={{
                  label: "View Setup",
                  onClick: () => navigate(`/workstations/${uc.slug}`),
                  testId: `laptop-add-${uc.slug}`,
                }}
                compact
              />
            );
          })}
        </div>

        {/* Single section CTA under the row */}
        <div className="mt-10 flex justify-center">
          <button
            data-testid="view-all-setups"
            onClick={() => navigate(sectionCta.link)}
            className="inline-flex items-center gap-2 rounded-full border border-[#333] bg-transparent text-white font-semibold text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
          >
            {sectionCta.label} <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        {/* Perks bar — refined specification-style benefit row on
            mobile (no heavy card enclosure, subtle dividers, smaller
            line icons); reverts to the approved boxed-card treatment
            at `sm+`. Help perk (Need Help Choosing) gains a subtle
            orange accent border on mobile so it reads as actionable
            while staying quiet. */}
        <div data-testid="laptop-perks-bar" className="mt-10 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-3 sm:gap-3">
          {perks.map((p, i) => {
            const Icon = Icons[p.icon] || Icons.Sparkles;
            const help = isHelpChoosing(p.title);
            const content = (
              <>
                <Icon
                  className={`w-5 h-5 sm:w-7 sm:h-7 shrink-0 ${help ? "text-[#FF6600]" : "text-white"}`}
                  strokeWidth={1.75}
                />
                <div className="min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-white">{p.title}</div>
                  <div className="text-[11px] text-[#A3A3A3] mt-1 leading-snug">{p.sub}</div>
                </div>
              </>
            );
            const wrapperClass =
              "bg-transparent sm:bg-[#141414] " +
              "border-b sm:border sm:border-[#262626] " +
              (help ? "border-[#FF6600]/30 sm:border-[#FF6600]/30 " : "border-[#1F1F1F] ") +
              "rounded-none sm:rounded-2xl px-2 pt-2 pb-3 sm:p-4 " +
              "flex items-center gap-3 transition-colors " +
              (help ? "hover:border-[#FF6600] " : "");
            if (help && whatsappUrl) {
              // WhatsApp help perk is a call-to-action (chat with support) —
              // treated as a floating/CTA affordance and explicitly excluded
              // from scroll-reveal, same rule that already applies to Hero,
              // FinalCTA, and the brands marquee. Wrapping it in <Reveal>
              // also hit a subtle React edge case: the element type flips
              // from <div> to <a> once the settings API resolves the URL,
              // remounting the component, which reset inView=false but the
              // fresh IntersectionObserver never re-fired on an element
              // that was already fully in viewport — leaving it stuck at
              // opacity:0 while its href stayed clickable. This exclusion
              // sidesteps that whole class of bug.
              return (
                <a
                  key={i}
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`laptop-perk-${i}`}
                  className={wrapperClass}
                >
                  {content}
                </a>
              );
            }
            return (
              <Reveal
                key={i}
                delay={Math.min(i * 25, 100)}
                data-testid={`laptop-perk-${i}`}
                className={wrapperClass}
              >
                {content}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
